import { useState, useEffect, useCallback } from 'react'
import { formsApi, type ApiSubmission } from '../utils/api'

interface Props {
  siteId: string | null
  onClose: () => void
}

export function SubmissionsModal({ siteId, onClose }: Props) {
  const [submissions, setSubmissions] = useState<ApiSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [formFilter, setFormFilter] = useState('')
  const [selected, setSelected] = useState<ApiSubmission | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const data = await formsApi.list(siteId, formFilter || undefined)
      setSubmissions(data)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [siteId, formFilter])

  useEffect(() => { load() }, [load])

  async function handleDelete(id: string) {
    if (!siteId) return
    setDeleting(id)
    try {
      await formsApi.delete(siteId, id)
      setSubmissions(s => s.filter(x => x.id !== id))
      if (selected?.id === id) setSelected(null)
    } finally {
      setDeleting(null)
    }
  }

  function exportCsv() {
    if (!submissions.length) return
    const allKeys = Array.from(new Set(submissions.flatMap(s => Object.keys(s.data))))
    const header = ['Дата', 'Форма', 'Страница', ...allKeys]
    const rows = submissions.map(s => [
      new Date(s.createdAt).toLocaleString('ru'),
      s.formId,
      s.pageSlug,
      ...allKeys.map(k => String(s.data[k] ?? '')),
    ])
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'submissions.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const formIds = Array.from(new Set(submissions.map(s => s.formId)))

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '880px', maxWidth: '96vw', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>Заявки с форм</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {loading ? 'Загрузка...' : `${submissions.length} заявок`}
            </div>
          </div>
          {formIds.length > 1 && (
            <select
              value={formFilter}
              onChange={e => setFormFilter(e.target.value)}
              style={{ padding: '6px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-primary)', cursor: 'pointer' }}
            >
              <option value="">Все формы</option>
              {formIds.map(id => <option key={id} value={id}>{id}</option>)}
            </select>
          )}
          <button
            onClick={exportCsv}
            disabled={!submissions.length}
            style={{ padding: '6px 14px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            CSV
          </button>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

          {/* Table */}
          <div style={{ flex: 1, overflowY: 'auto', borderRight: selected ? '1px solid var(--border)' : 'none' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Загрузка...</div>
            ) : error ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#f87171' }}>{error}</div>
            ) : submissions.length === 0 ? (
              <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Заявок пока нет</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-2)' }}>
                    <th style={thStyle}>Дата</th>
                    <th style={thStyle}>Страница</th>
                    <th style={thStyle}>Данные</th>
                    <th style={{ ...thStyle, width: '60px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(sub => (
                    <tr
                      key={sub.id}
                      onClick={() => setSelected(s => s?.id === sub.id ? null : sub)}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: selected?.id === sub.id ? 'rgba(91,106,240,0.08)' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                    >
                      <td style={tdStyle}>
                        <div style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                          {new Date(sub.createdAt).toLocaleDateString('ru', { day: '2-digit', month: '2-digit' })}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', opacity: 0.7 }}>
                          {new Date(sub.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <code style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{sub.pageSlug}</code>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '320px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {Object.entries(sub.data).slice(0, 4).map(([k, v]) => (
                            <span key={k} style={{ background: 'var(--bg-3)', borderRadius: '4px', padding: '1px 6px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                              <span style={{ opacity: 0.6 }}>{k}: </span>{String(v)}
                            </span>
                          ))}
                          {Object.keys(sub.data).length > 4 && (
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>+{Object.keys(sub.data).length - 4}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(sub.id) }}
                          disabled={deleting === sub.id}
                          style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', opacity: deleting === sub.id ? 0.4 : 1, padding: '2px 6px' }}
                          title="Удалить"
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail pane */}
          {selected && (
            <div style={{ width: '280px', overflowY: 'auto', padding: '16px', flexShrink: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '12px' }}>Детали заявки</div>
              <div style={{ marginBottom: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
                {new Date(selected.createdAt).toLocaleString('ru')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(selected.data).map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{k}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', wordBreak: 'break-word', background: 'var(--bg-2)', borderRadius: '6px', padding: '6px 8px' }}>
                      {String(v) || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>пусто</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-muted)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'top',
  color: 'var(--text-primary)',
}
